import { z } from "zod";

/**
 * Track C, C1 (issue #378). Body schema for POST /meal-plans/:id/prep-tasks.
 * `.strict()` per the security-boundary pattern used across this package.
 *
 * `coversSlotIds` is a plain informational list (which meal slots this
 * task's work feeds) -- the route validates every id belongs to the SAME
 * meal plan, but otherwise this array has no FK, same as
 * GroceryItem.sourceRecipeIds.
 *
 * `cookedBySlotId` is the owner decision this schema encodes for how the
 * MealSlot self-relation (schema.prisma's `cookedBySlot`/`coveredSlots`)
 * actually gets populated: the issue names GET/POST/PATCH prep-task routes
 * only, with no dedicated slot-coverage endpoint, so wiring the coverage
 * relationship happens as a side effect of creating the prep task that
 * describes it -- "chop veg + cook a double batch on Sunday, covering
 * Tuesday and Thursday" naturally IS the moment you'd declare which slot
 * did the cooking. When present, `coversSlotIds` must be non-empty (a
 * cooking slot with nothing to cover isn't a coverage relationship). The
 * route (apps/api/src/routes/prep-tasks.ts) does the plan-membership,
 * self-reference, and cycle checks that can't be expressed here.
 */
export const createPrepTaskSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    estimatedMinutes: z.number().int().positive().max(24 * 60),
    sortOrder: z.number().int().min(0).optional(),
    coversSlotIds: z.array(z.string().min(1)).max(50).optional(),
    cookedBySlotId: z.string().min(1).optional(),
  })
  .strict()
  .refine(
    (data) => !data.cookedBySlotId || (data.coversSlotIds && data.coversSlotIds.length > 0),
    {
      message: "cookedBySlotId requires at least one coversSlotIds entry",
      path: ["cookedBySlotId"],
    },
  );

export type CreatePrepTaskInput = z.infer<typeof createPrepTaskSchema>;

/**
 * Body schema for PATCH /prep-tasks/:id -- the completedAt toggle. `true`
 * stamps `completedAt` to now; `false` clears it back to null. Deliberately
 * the only mutable field on this route (see the issue's literal route
 * list) -- coverage wiring is create-time-only, via POST above.
 */
export const updatePrepTaskSchema = z
  .object({
    completed: z.boolean(),
  })
  .strict();

export type UpdatePrepTaskInput = z.infer<typeof updatePrepTaskSchema>;
